/**
 * Emergency dictation vault — survives crash / remount / accidental clear.
 * Never delete except after confirmed durable save (clearDictationVault).
 * Dual-write: localStorage (primary) + sessionStorage (mirror fallback #5).
 */

export const DICTATION_VAULT_KEY = "dictationEmergencyVault";
const MAX_CHARS = 200000;

function storageGet(store, key) {
  try {
    return store.getItem(key);
  } catch (_e) {
    return null;
  }
}

function storageSet(store, key, value) {
  try {
    store.setItem(key, value);
    return true;
  } catch (_e) {
    return false;
  }
}

function storageRemove(store, key) {
  try {
    store.removeItem(key);
  } catch (_e) {}
}

function parseVault(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.text !== "string" || !parsed.text.trim()) return null;
    return parsed;
  } catch (_e) {
    return null;
  }
}

export function readDictationVault() {
  const primary = parseVault(storageGet(localStorage, DICTATION_VAULT_KEY));
  if (primary) return primary;
  return parseVault(storageGet(sessionStorage, DICTATION_VAULT_KEY));
}

export function writeDictationVault(text, { source = "voice" } = {}) {
  const safe = String(text || "");
  if (!safe.trim()) return false;
  const payload = JSON.stringify({
    text: safe.length > MAX_CHARS ? safe.slice(-MAX_CHARS) : safe,
    source,
    updatedAt: Date.now(),
  });
  const okLocal = storageSet(localStorage, DICTATION_VAULT_KEY, payload);
  const okSession = storageSet(sessionStorage, DICTATION_VAULT_KEY, payload);
  if (!okLocal && !okSession) {
    console.warn("[dictationVault] write failed on both stores");
    return false;
  }
  return true;
}

export function appendDictationVault(chunk, { source = "voice" } = {}) {
  const piece = String(chunk || "").trim();
  if (!piece) return false;
  const prev = readDictationVault();
  const base = prev?.text || "";
  const glue = base && !base.endsWith(" ") && !base.endsWith("\n") ? " " : "";
  return writeDictationVault(`${base}${glue}${piece}`, { source });
}

export function clearDictationVault() {
  storageRemove(localStorage, DICTATION_VAULT_KEY);
  storageRemove(sessionStorage, DICTATION_VAULT_KEY);
}

/** Restore if vault is newer than `minAgeMs` ago and has substance. */
export function vaultWorthRestoring({ maxAgeMs = 48 * 60 * 60 * 1000, minChars = 8 } = {}) {
  const v = readDictationVault();
  if (!v) return null;
  if (v.text.trim().length < minChars) return null;
  if (typeof v.updatedAt === "number" && Date.now() - v.updatedAt > maxAgeMs) return null;
  return v;
}
